/**
 * SSADashboard Component
 * 
 * Detail view for a safety assessment showing work products and traceability.
 */

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Shield,
  FileText,
  GitBranch,
  Download,
  CheckCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SFHAEditor } from './SFHAEditor';
import { FTAEditor } from './FTAEditor';
import { FMEAEditor } from './FMEAEditor';
import { TraceabilityGraph } from './TraceabilityGraph';
import { SSAReportGenerator } from './SSAReportGenerator';
import { safetyApi } from '@/services/safetyApi';
import LinkAIButton from '@/components/LinkAIButton';
import { logger } from '../../utils/logger';
import type { 
  SafetyAssessment, 
  SFHAWorkProduct, 
  FTAWorkProduct, 
  FMEAWorkProduct, 
  CMAWorkProduct,
} from '@aerotage/shared';

type ViewType = 'overview' | 'sfha' | 'fta' | 'fmea' | 'cma' | 'traceability' | 'report';

interface SSADashboardProps {
  assessment: SafetyAssessment;
  onBack: () => void;
}

export const SSADashboard: React.FC<SSADashboardProps> = ({ assessment, onBack }) => {
  const [currentView, setCurrentView] = useState<ViewType>('overview');
  const [sfha, setSFHA] = useState<SFHAWorkProduct | null>(null);
  const [ftas, setFTAs] = useState<FTAWorkProduct[]>([]);
  const [fmea, setFMEA] = useState<FMEAWorkProduct | null>(null);
  const [cma, setCMA] = useState<CMAWorkProduct | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadWorkProducts();
  }, [assessment.assessmentId]);

  const loadWorkProducts = async () => {
    setIsLoading(true);
    try {
      // Load all work products in parallel
      // safetyApi functions require both projectId and assessmentId
      const [sfhaData, ftaData, fmeaData, cmaData] = await Promise.allSettled([
        safetyApi.getSFHA(assessment.projectId, assessment.assessmentId),
        safetyApi.listFTAs(assessment.projectId, assessment.assessmentId),
        safetyApi.getFMEA(assessment.projectId, assessment.assessmentId),
        safetyApi.getCMA(assessment.projectId, assessment.assessmentId),
      ]);

      if (sfhaData.status === 'fulfilled') setSFHA(sfhaData.value);
      if (ftaData.status === 'fulfilled') setFTAs(ftaData.value);
      if (fmeaData.status === 'fulfilled') setFMEA(fmeaData.value);
      if (cmaData.status === 'fulfilled') setCMA(cmaData.value);
    } catch (err) {
      logger.error('Failed to load work products:', err);
    } finally {
      setIsLoading(false);
    }
  };


  const renderContent = () => {
    switch (currentView) {
      case 'sfha':
        return <SFHAEditor data={sfha} onUpdate={loadWorkProducts} />;
      case 'fta':
        return <FTAEditor data={ftas} onUpdate={loadWorkProducts} />;
      case 'fmea':
        return <FMEAEditor data={fmea} onUpdate={loadWorkProducts} />;
      case 'traceability':
        return <TraceabilityGraph assessmentId={assessment.assessmentId} />;
      case 'report':
        return <SSAReportGenerator assessment={assessment} />;
      default:
        return (
          <OverviewContent
            assessment={assessment}
            sfha={sfha}
            ftas={ftas}
            fmea={fmea}
            cma={cma}
            onNavigate={setCurrentView}
          />
        );
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Safety
          </Button>
          <div className="h-6 w-px bg-border-primary" />
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-lg font-semibold text-foreground">
                {assessment.systemName}
              </h1>
              <p className="text-sm text-muted-foreground">
                {assessment.stage.toUpperCase()} • {assessment.status}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LinkAIButton
            artifactType="ssa"
            artifactId={assessment.assessmentId}
            artifactName={assessment.systemName}
            projectId={assessment.projectId}
            variant="outline"
          />
          <Button variant="secondary" onClick={() => setCurrentView('report')}>
            <Download className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
        </div>
      </div>

      {/* Navigation tabs */}
      <div className="flex items-center gap-1 px-6 py-2 border-b border-border bg-card">
        <NavTab
          active={currentView === 'overview'}
          onClick={() => setCurrentView('overview')}
        >
          Overview
        </NavTab>
        <NavTab
          active={currentView === 'sfha'}
          onClick={() => setCurrentView('sfha')}
          status={sfha ? 'active' : 'empty'}
        >
          SFHA
        </NavTab>
        <NavTab
          active={currentView === 'fta'}
          onClick={() => setCurrentView('fta')}
          status={ftas.length > 0 ? 'active' : 'empty'}
        >
          FTA ({ftas.length})
        </NavTab>
        <NavTab
          active={currentView === 'fmea'}
          onClick={() => setCurrentView('fmea')}
          status={fmea ? 'active' : 'empty'}
        >
          FMEA
        </NavTab>
        <NavTab
          active={currentView === 'cma'}
          onClick={() => setCurrentView('cma')}
          status={cma ? 'active' : 'empty'}
        >
          CMA
        </NavTab>
        <div className="flex-1" />
        <NavTab
          active={currentView === 'traceability'}
          onClick={() => setCurrentView('traceability')}
        >
          <GitBranch className="w-4 h-4 mr-1" />
          Traceability
        </NavTab>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
};

// Navigation tab component
interface NavTabProps {
  active: boolean;
  onClick: () => void;
  status?: 'empty' | 'active' | 'complete';
  children: React.ReactNode;
}

const NavTab: React.FC<NavTabProps> = ({ active, onClick, status, children }) => (
  <Button
    variant="ghost"
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-t-md flex items-center gap-1 ${
      active
        ? 'bg-background text-foreground border-b-2 border-primary'
        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
    }`}
  >
    {status === 'empty' && <span className="w-2 h-2 rounded-full bg-muted" />}
    {status === 'active' && <span className="w-2 h-2 rounded-full bg-warning" />}
    {status === 'complete' && <span className="w-2 h-2 rounded-full bg-success" />}
    {children}
  </Button>
);

// Overview content component
interface OverviewContentProps {
  assessment: SafetyAssessment;
  sfha: SFHAWorkProduct | null;
  ftas: FTAWorkProduct[];
  fmea: FMEAWorkProduct | null;
  cma: CMAWorkProduct | null;
  onNavigate: (view: ViewType) => void;
}

const OverviewContent: React.FC<OverviewContentProps> = ({
  assessment,
  sfha,
  ftas,
  fmea,
  cma,
  onNavigate,
}) => {
  const traceability = assessment.traceabilitySummary;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <WorkProductCard
          title="SFHA"
          subtitle="System Functional Hazard Assessment"
          status={sfha ? 'active' : 'not_started'}
          stats={sfha ? {
            functions: sfha.functions.length,
            failureConditions: sfha.failureConditions.length,
          } : undefined}
          onClick={() => onNavigate('sfha')}
        />
        <WorkProductCard
          title="FTA"
          subtitle="Fault Tree Analysis"
          status={ftas.length > 0 ? 'active' : 'not_started'}
          stats={ftas.length > 0 ? {
            trees: ftas.length,
            events: ftas.reduce((acc, f) => acc + f.events.length, 0),
          } : undefined}
          onClick={() => onNavigate('fta')}
        />
        <WorkProductCard
          title="FMEA"
          subtitle="Failure Modes and Effects Analysis"
          status={fmea ? 'active' : 'not_started'}
          stats={fmea ? {
            items: fmea.items.length,
          } : undefined}
          onClick={() => onNavigate('fmea')}
        />
        <WorkProductCard
          title="CMA"
          subtitle="Common Mode Analysis"
          status={cma ? 'active' : 'not_started'}
          stats={cma ? {
            analyses: cma.analyses.length,
          } : undefined}
          onClick={() => onNavigate('cma')}
        />
      </div>

      {/* Traceability Summary */}
      {traceability && (
        <div className="bg-card border border-border rounded-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
              <GitBranch className="w-5 h-5" />
              Traceability Summary
            </h2>
            <Button variant="ghost" size="sm" onClick={() => onNavigate('traceability')}>
              View Graph
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatItem label="Functions" value={traceability.functionCount} />
            <StatItem label="Failure Conditions" value={traceability.failureConditionCount} />
            <StatItem label="FTA Events" value={traceability.ftaEventCount} />
            <StatItem label="FMEA Items" value={traceability.fmeaItemCount} />
          </div>
          {traceability.unlinkedEventsCount > 0 && (
            <div className="mt-4 p-3 bg-warning/10 border border-warning/30 rounded-md flex items-center gap-2 text-warning">
              <AlertTriangle className="w-5 h-5" />
              <span>
                {traceability.unlinkedEventsCount} basic events need reliability data links
              </span>
            </div>
          )}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Completeness Score</span>
              <span className="text-foreground font-medium">
                {traceability.completenessScore}%
              </span>
            </div>
            <div className="h-2 bg-background rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  traceability.completenessScore >= 80
                    ? 'bg-success'
                    : traceability.completenessScore >= 50
                    ? 'bg-warning'
                    : 'bg-destructive'
                } ${
                  traceability.completenessScore >= 100 ? 'w-full' :
                  traceability.completenessScore >= 90 ? 'w-11/12' :
                  traceability.completenessScore >= 80 ? 'w-4/5' :
                  traceability.completenessScore >= 75 ? 'w-3/4' :
                  traceability.completenessScore >= 66 ? 'w-2/3' :
                  traceability.completenessScore >= 50 ? 'w-1/2' :
                  traceability.completenessScore >= 33 ? 'w-1/3' :
                  traceability.completenessScore >= 25 ? 'w-1/4' :
                  traceability.completenessScore >= 20 ? 'w-1/5' :
                  traceability.completenessScore >= 10 ? 'w-1/12' : 'w-0'
                }`}
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-medium text-foreground mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" onClick={() => onNavigate('sfha')}>
            {sfha ? 'Continue SFHA' : 'Start SFHA'}
          </Button>
          <Button variant="secondary" onClick={() => onNavigate('fta')}>
            {ftas.length > 0 ? 'Continue FTA' : 'Start FTA'}
          </Button>
          <Button variant="secondary" onClick={() => onNavigate('report')}>
            Generate Report
          </Button>
        </div>
      </div>
    </div>
  );
};

// Work product card component
interface WorkProductCardProps {
  title: string;
  subtitle: string;
  status: 'not_started' | 'active' | 'complete';
  stats?: Record<string, number>;
  onClick: () => void;
}

const WorkProductCard: React.FC<WorkProductCardProps> = ({
  title,
  subtitle,
  status,
  stats,
  onClick,
}) => {
  const statusConfig = {
    not_started: {
      icon: <Clock className="w-5 h-5 text-muted-foreground" />,
      label: 'Not Started',
      color: 'text-muted-foreground',
    },
    active: {
      icon: <FileText className="w-5 h-5 text-warning" />,
      label: 'In Progress',
      color: 'text-warning',
    },
    complete: {
      icon: <CheckCircle className="w-5 h-5 text-success" />,
      label: 'Complete',
      color: 'text-success',
    },
  };

  const { icon, label, color } = statusConfig[status];

  return (
    <div
      className="bg-card border border-border rounded-lg p-4 hover:border-primary transition-colors cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        {icon}
      </div>
      <p className="text-sm text-muted-foreground mb-3">{subtitle}</p>
      <p className={`text-xs ${color}`}>{label}</p>
      {stats && (
        <div className="mt-3 pt-3 border-t border-border">
          {Object.entries(stats).map(([key, value]) => (
            <div key={key} className="flex justify-between text-sm">
              <span className="text-muted-foreground capitalize">{key}</span>
              <span className="text-foreground font-medium">{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Stat item component
const StatItem: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="text-center">
    <div className="text-2xl font-bold text-foreground">{value}</div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </div>
);
